import React, { useState, useEffect, useCallback } from "react";
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import SuperPatientProfile from "./SuperPatientProfile";

const OPDList = () => {
  const navigate = useNavigate();

  const [isSuperPatientProfileOpen, setIsSuperPatientProfileOpen] = useState(false);
  const [profileId, setProfileId] = useState(null);

  // Core state
  const [opdData, setOpdData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // UI state
  const [expandedTiles, setExpandedTiles] = useState({});

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, itemsPerPage]);

  // Fetch data
  const fetchOPDData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      });

      const response = await API.get(`/api/doctor/opd?${params}`);

      if (response.data.success) {
        setOpdData(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalRecords(response.data.pagination.totalRecords);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching OPD data");
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchOPDData();
  }, [fetchOPDData]);

  // Handlers
  const toggleTile = useCallback((id) => {
    setExpandedTiles((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleCheckup = useCallback(
    (id) => {
      navigate(`/dashboard/checkup/${id}`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (id) => {
      navigate(`/dashboard/edit-opd/${id}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Are you sure you want to delete this OPD?")) return;

      try {
        await API.delete(`/api/doctor/delete-opd-patientrecord/${id}`);
        toast.success("OPD deleted successfully!");
        fetchOPDData();
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to delete OPD");
      }
    },
    [fetchOPDData]
  );

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Helper functions
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  const renderField = (value, fallback = "N/A") => {
    if (Array.isArray(value)) return value.join(", ") || fallback;
    return value || fallback;
  };

  if (loading && opdData.length === 0) {
    return <Loading />;
  }

  return (
    <div className=" mx-auto p-6 space-y-6">
      {/* Header */}

      <div className="text-center bg-white rounded-2xl shadow-sm p-6 border">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          All OPD Records
        </h1>
        <p className="text-gray-600 text-lg">Check and manage all patient OPD records</p>
      </div>

      {/* Search & Stats */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, mobile, or OPD number..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Items per page */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              {[5, 10, 15, 20, 25].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Stats */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {totalRecords}
              </div>
              <div className="text-sm text-gray-600">Total Records</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {currentPage}
              </div>
              <div className="text-sm text-gray-600">Current Page</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {totalPages}
              </div>
              <div className="text-sm text-gray-600">Total Pages</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {opdData.length}
              </div>
              <div className="text-sm text-gray-600">Showing</div>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-600 font-medium">Error: {error}</span>
          <button
            onClick={fetchOPDData}
            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading records...</span>
          </div>
        </div>
      )}

      {/* Records Grid */}
      {!loading && opdData.length > 0 ? (
        <div className="space-y-4">
          {opdData.map((opd) => (
            <div
              key={opd._id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow"
            >
              {/* Card Header */}
              <div className="p-6 border-b">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Basic Info */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">OPD Number</div>
                      <div className="font-semibold text-blue-600">
                        {opd.opdNumber}
                        <span
                          className={`inline-block ml-2 w-2.5 h-2.5 rounded-full ${opd?.isCompleted
                            ? "bg-red-500"
                            : "bg-green-500"
                            }`}
                          title={
                            opd?.isCompleted === true ? "Inactive" : "Active"
                          }
                        >
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500"

                      >Patient Name</div>
                      <div className="font-medium"
                        onClick={() => {
                          setProfileId(opd?.user?._id)
                          setIsSuperPatientProfileOpen(true)
                          console.log("user id", opd?.user?._id)
                        }}>
                        {renderField(opd.userDetails?.name)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Age</div>
                      <div className="font-medium">{renderField(opd.age)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Date</div>
                      <div className="font-medium">
                        {formatDate(opd.createdAt)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {opd.checkupInfo ? <button
                      onClick={() => handleCheckup(opd._id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Checkup
                    </button>
                      :
                      <button
                        onClick={() => handleEdit(opd._id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        Edit Case
                      </button>
                    }
                    <button
                      onClick={() => toggleTile(opd._id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      {expandedTiles[opd._id] ? "Less" : "More"}
                    </button>
                  </div>
                </div>

                {/* Chief Complaint */}
                <div className="mt-4">
                  <div className="text-sm text-gray-500">Chief Complaint</div>
                  <div className="font-medium text-gray-900">
                    {renderField(opd.checkupInfo)}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedTiles[opd._id] && (
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Patient Details */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-3">
                        Patient Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Email:</span>{" "}
                          {renderField(opd.user?.email)}
                        </div>
                        <div>
                          <span className="text-gray-500">Contact:</span>{" "}
                          {renderField(opd.userDetails?.contact?.mobile)}
                        </div>
                        <div>
                          <span className="text-gray-500">Gender:</span>{" "}
                          {renderField(opd.userDetails?.gender)}
                        </div>
                        <div>
                          <span className="text-gray-500">Address:</span>{" "}
                          {renderField(opd.userDetails?.contact?.address)}
                        </div>
                      </div>
                    </div>

                    {/* Medical Details */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-3">
                        Medical Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Blood Group:</span>{" "}
                          {renderField(opd.medicalDetails?.bloodGroup)}
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Medical History:
                          </span>{" "}
                          {renderField(opd.medicalDetails?.medicalHistory)}
                        </div>
                        <div>
                          <span className="text-gray-500">Dental History:</span>{" "}
                          {renderField(opd.medicalDetails?.pastDentalHistory)}
                        </div>
                      </div>
                    </div>

                    {/* Lifestyle */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-3">
                        Lifestyle
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Smoking:</span>{" "}
                          {renderField(
                            opd.medicalDetails?.lifestyleAndHabits
                              ?.smokingStatus
                          )}
                        </div>
                        <div>
                          <span className="text-gray-500">Alcohol:</span>{" "}
                          {renderField(
                            opd.medicalDetails?.lifestyleAndHabits?.alcoholUse
                          )}
                        </div>
                        <div>
                          <span className="text-gray-500">Tobacco:</span>{" "}
                          {renderField(
                            opd.medicalDetails?.lifestyleAndHabits?.tobacco
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6 pt-4 border-t">
                    <button
                      onClick={() => handleEdit(opd._id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Edit Case
                    </button>
                    <button
                      onClick={() => handleDelete(opd._id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      Delete Case
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !loading && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <div className="text-gray-500">
              No OPD records found matching your search criteria.
            </div>
          </div>
        )
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
              {totalRecords} results
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 text-sm rounded-lg ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 border hover:bg-gray-50"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
      <SuperPatientProfile
        open={isSuperPatientProfileOpen}
        onClose={() => setIsSuperPatientProfileOpen(false)}
        patientId={profileId}
      />
    </div>
  );
};

export default OPDList;
